import {
  Dropdown,
  Option,
  SelectionEvents,
  OptionOnSelectData,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components';
import { AddRegular, DeleteRegular, MoreHorizontalFilled } from '@fluentui/react-icons';
import { useContext, useRef, useState } from 'react';

import { ConfirmDialog, TConfirmDialogState } from '@shared/components';
import { TERMINOLOGY } from '@shared/i18n';

import { DocumentContext, TVariant, useCreateVariant, useDeleteVariant, getVariant } from '@modules/documents';

import { isTDraft } from '../../types/items.types';
import { useCanEdit } from '@modules/access/hooks/useCanEdit.hook';
import { TSelectFolderDialogState } from '../../types/folders.types';
import SelectFolderDialog from '../dialogs/SelectFolderDialog';

const VariantInfo = () => {
  const { doc, version, resolvedItemNames, variant, permissions, changeVariant } = useContext(DocumentContext);
  const { canEdit } = useCanEdit(permissions.accessRight);
  const selectedOptions = variant ? [variant.folder] : ['default'];
  let label: string = 'Default';
  if (variant) label = resolvedItemNames[variant.folder] || 'Unknown';

  const refVersion = useRef(version);
  refVersion.current = version;
  const handleOptionSelect = (event: SelectionEvents, data: OptionOnSelectData) => {
    changeVariant(getVariant(data.selectedOptions[0], refVersion.current));
  };

  const [selectFolderDialogState, setSelectFolderDialogState] = useState<TSelectFolderDialogState | undefined>(
    undefined
  );
  const createVariant = useCreateVariant();
  const handleCreateVariant = () => {
    setSelectFolderDialogState({
      isOpen: true,
      affectedItems: [],
      select: async (targetId: string) => {
        const res = await createVariant.mutateAsync({
          docId: doc._id,
          folder: targetId,
          srcVariantFolder: variant?.folder,
        });
        setSelectFolderDialogState(undefined);
        changeVariant(res?.variant);
      },
    });
  };

  const handleCreateDefaultVariant = async () => {
    await createVariant.mutateAsync({ docId: doc._id, srcVariantFolder: variant?.folder });
    changeVariant();
  };

  const [confirmDialogState, setConfirmDialogState] = useState<TConfirmDialogState | undefined>(undefined);

  const deleteVariant = useDeleteVariant();
  const handleDeleteVariant = () => {
    const dialogState: TConfirmDialogState = {
      isOpen: true,
      title: `Delete variant?`,
      content: 'Are you sure you want to delete this variant?',
      buttonLabel: 'Delete',
      onConfirm: async () => {
        // async to move button to loading state
        let newVariant: TVariant | undefined = undefined;
        if (!variant || !refVersion.current?.hasDefaultVariant)
          newVariant = refVersion.current?.variants.find((v) => v.folder !== variant?.folder);
        await changeVariant(newVariant);

        try {
          await deleteVariant.mutateAsync({ docId: doc._id, folder: variant?.folder });
          setConfirmDialogState({ ...dialogState, isOpen: false });
        } catch (error) {
          await changeVariant(variant);
        }
      },
    };
    setConfirmDialogState(dialogState);
  };
  const disabledDeleteVariant =
    !version || version.variants.length === 0 || (version.variants.length === 1 && !version.hasDefaultVariant);

  return (
    <div>
      <SelectFolderDialog
        title="Select jurisdiction for variant"
        buttonLabel="Select"
        sourceId={doc.resolvedPath[0]._id}
        navigatableFolderTypes={['NORMAL', 'COUNTRY', 'SNAPSHOT']}
        requireFs={false}
        state={selectFolderDialogState}
        setState={setSelectFolderDialogState}
      />
      <p className="mb-2">{TERMINOLOGY.block.upper} variant:</p>
      <div className="flex justify-center items-center space-x-2">
        <Dropdown
          selectedOptions={selectedOptions}
          value={label}
          onOptionSelect={handleOptionSelect}
          appearance="outline"
        >
          {version?.hasDefaultVariant && <Option value="default">Default</Option>}
          {version?.variants?.map((variant) => (
            <Option value={variant.folder} key={variant.folder}>
              {resolvedItemNames[variant.folder] || 'Unknown'}
            </Option>
          ))}
        </Dropdown>

        <span className="-mb-0.5">
          <ConfirmDialog state={confirmDialogState} setState={setConfirmDialogState} />
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                size="small"
                appearance="transparent"
                icon={<MoreHorizontalFilled />}
                disabled={!isTDraft(version) || !canEdit}
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem icon={<AddRegular />} onClick={handleCreateVariant}>
                  Create
                </MenuItem>
                {!version?.hasDefaultVariant && (
                  <MenuItem icon={<AddRegular />} onClick={handleCreateDefaultVariant}>
                    Create default
                  </MenuItem>
                )}

                <MenuItem icon={<DeleteRegular />} disabled={disabledDeleteVariant} onClick={handleDeleteVariant}>
                  Delete
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </span>
      </div>
    </div>
  );
};

export default VariantInfo;
